import { overlays } from '@data/overlays.js';
import { NavLink } from 'react-router';
import PropTypes from 'prop-types';
import { graphics } from '@data/graphics.js';
import { information } from '@data/information.js';
import { chatbots } from '@data/chatbots.js';
import { useCallback } from 'preact/hooks';
import './global-sidebar-menu.css';

GlobalSidebarMenu.propTypes = {
  searchValue: PropTypes.string,
  onLinkClick: PropTypes.func,
};

const appEnv = import.meta.env.VITE_APP_ENV || 'dev';

const overrideShow = appEnv === 'dev' || appEnv === 'qa';

export default function GlobalSidebarMenu({
  searchValue = '',
  onLinkClick = () => {},
}) {
  const sortItems = (a, b) => {
    if (a.hasOwnProperty('order') && b.hasOwnProperty('order')) {
      return a.order - b.order;
    }

    return a.name.localeCompare(b.name);
  };

  const allOverlays = Object.values(overlays)
    .filter((overlay) => overlay.category !== 'progress')
    .filter((overlay) => overlay.show || overrideShow)
    .filter((overlay) => {
      if (searchValue.length > 0) {
        return overlay.name.toLowerCase().includes(searchValue);
      }
      return true;
    })
    .sort(sortItems);

  const progressBars = Object.values(overlays)
    .filter((overlay) => overlay.category === 'progress')
    .filter((overlay) => overlay.show || overrideShow)
    .filter((overlay) => {
      if (searchValue.length > 0) {
        return overlay.name.toLowerCase().includes(searchValue);
      }
      return true;
    })
    .sort(sortItems);

  const graphicsBundles = Object.values(graphics)
    .filter((bundle) => bundle.show || overrideShow)
    .filter((bundle) => {
      if (searchValue.length > 0) {
        return bundle.name.toLowerCase().includes(searchValue);
      }
      return true;
    })
    .sort(sortItems);

  const informationItems = Object.values(information)
    .filter((item) => item.show || overrideShow)
    .filter((item) => {
      if (searchValue.length > 0) {
        return item.name.toLowerCase().includes(searchValue);
      }
      return true;
    })
    .sort(sortItems);

  const chatbotItems = Object.values(chatbots)
    .filter((chatbot) => chatbot.show || overrideShow)
    .filter((chatbot) => {
      if (searchValue.length > 0) {
        return chatbot.name.toLowerCase().includes(searchValue);
      }
      return true;
    })
    .sort(sortItems);

  const overlaySections = [
    {
      name: 'Progress Bars',
      children: progressBars.map((progressBar) => {
        return {
          name: progressBar.name.replace('Progress Bar', ''),
          link: progressBar.link,
          updated: progressBar.isUpdated,
          new: progressBar.isNew,
          show: progressBar.show,
        };
      }),
    },
    {
      name: 'Fundraising Tools',
      children: allOverlays.map((overlay) => {
        return {
          name: overlay.name,
          link: overlay.link,
          updated: overlay.isUpdated,
          new: overlay.isNew,
          show: overlay.show,
        };
      }),
    },
  ];

  const graphicsBundleSections = graphicsBundles.map((bundle) => {
    return {
      name: bundle.name,
      link: bundle.link,
      updated: bundle.isUpdated,
      new: bundle.isNew,
      featured: bundle.isFeatured,
      show: bundle.show,
    };
  });

  const informationSection = informationItems.map((item) => {
    return {
      name: item.name,
      link: item.link,
      updated: item.isUpdated,
      new: item.isNew,
      featured: item.isFeatured,
      show: item.show,
    };
  });

  const chatbotSection = chatbotItems.map((item) => {
    return {
      name: item.name.replace(' Chatbot', ''),
      link: item.link,
      updated: item.isUpdated,
      new: item.isNew,
      featured: item.isFeatured,
      show: item.show,
    };
  });

  const formatName = useCallback((name) => {
    if (name === 'Leaderboard Overlay') return 'Leaderboard';

    return name;
  }, []);

  const makeMenuItem = (item) => {
    const innerItem = (
      <>
        {formatName(item.name)}
        {item.new && (
          <span className="px-3 sj-light-blue text-white badge-sm w-[fit-content] font-bold skew-x-[-14deg]">
            <span className="inline-block skew-x-[14deg]">NEW</span>
          </span>
        )}
        {item.updated && (
          <span className="px-3 sj-dark-yellow text-black badge-sm w-[fit-content] font-bold skew-x-[-14deg]">
            <span className="inline-block skew-x-[14deg]">UPDATED</span>
          </span>
        )}
        {item.featured && (
          <span className="px-3 sj-primary badge-sm w-[fit-content] font-bold skew-x-[-14deg]">
            <span className="inline-block skew-x-[14deg]">FEATURED</span>
          </span>
        )}
      </>
    );

    return (
      <>
        <li className="py-1 relative lg:flex hidden" key={item.link}>
          <NavLink
            unstable_viewTransition
            to={item.link}
            className={
              item.className
                ? `flex flex-row justify-between leading-[1.6] ${item.className}`
                : `flex flex-row justify-between leading-[0.8] ${!item.show && overrideShow && 'text-red-600'}`
            }
          >
            {innerItem}
          </NavLink>
        </li>
        <li
          className="py-1 relative flex lg:hidden"
          key={`${item.link}-mobile`}
        >
          <NavLink
            unstable_viewTransition
            to={item.link}
            onClick={() => onLinkClick()}
            className={
              item.className
                ? `flex flex-row justify-between leading-[1.6] ${item.className}`
                : `flex flex-row justify-between leading-[0.8] ${!item.show && overrideShow && 'text-red-600'}`
            }
          >
            {innerItem}
          </NavLink>
        </li>
      </>
    );
  };

  const makeMenuSection = (section) => {
    return (
      <li key={section.name}>
        <h2 className="menu-title text-neutral-800">{section.name}</h2>
        <ul>{section.children.map((child) => makeMenuItem(child))}</ul>
      </li>
    );
  };

  const makeMenuItems = (item) => {
    if (Object.prototype.hasOwnProperty.call(item, 'children')) {
      return makeMenuSection(item);
    }
    return makeMenuItem(item);
  };

  const mainSection = [
    {
      name: 'Home',
      link: '/',
      className: 'font-bold text-lg text-neutral-800',
    },
    ...overlaySections,
    {
      name: 'Chatbots',
      children: chatbotSection,
    },
    {
      name: 'Graphics and Assets',
      children: graphicsBundleSections,
    },
    {
      name: 'Information',
      children: informationSection,
    },
  ];

  return (
    <ul className="global-sidebar-menu menu text-base-content w-full md:w-80 pb-10">
      {mainSection.map((section) => makeMenuItems(section))}
    </ul>
  );
}
